import React from "react";
import { Link } from "react-router-dom";

// ============================================================================
// SDIP MASTER MARK + BRAND LOCKUP SYSTEM
//
// Hierarchy the user must read instantly:
//   SDIP Technologies (parent)  →  SDIP AI (product)  →  ATLAS (engine)
//
// The master mark is the SDIP "S" symbol. The official asset has not been
// supplied yet, so SdipMark renders a clean geometric PLACEHOLDER "S" tile in
// the brand gradient (corporate blue → product green → intelligence cyan).
// SWAP: when the official file is provided, drop it at
//   /public/brand/sdip-s.svg
// and set BRAND_ASSET below to "/brand/sdip-s.svg" — the <img> path takes over
// automatically and the placeholder is retained only as an onError fallback.
// ============================================================================

const BRAND_ASSET = "/brand/sdip-master-symbol.webp"; // official SDIP master S symbol (corporate V3.1)

export function SdipMark({ size = 30, className = "" }: { size?: number; className?: string }) {
  const [failed, setFailed] = React.useState(false);
  if (BRAND_ASSET && !failed) {
    return (
      <img
        src={BRAND_ASSET}
        alt="SDIP"
        width={size}
        height={size}
        onError={() => setFailed(true)}
        className={`shrink-0 object-contain ${className}`}
        style={{ width: size, height: size }}
        data-testid="brand-mark"
      />
    );
  }
  return (
    <span className={`inline-flex shrink-0 ${className}`} data-testid="brand-mark" aria-label="SDIP">
      <svg width={size} height={size} viewBox="0 0 32 32" fill="none" role="img">
        <defs>
          <linearGradient id="sdip-mark-grad" x1="2" y1="2" x2="30" y2="30" gradientUnits="userSpaceOnUse">
            <stop stopColor="#1681ff" />
            <stop offset="0.55" stopColor="#25d366" />
            <stop offset="1" stopColor="#5ce1e6" />
          </linearGradient>
        </defs>
        <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="8.5" fill="url(#sdip-mark-grad)" />
        <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="8.5" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
        <path
          d="M21.2 11.2c-1.1-1.3-2.9-2-4.9-2-3 0-5 1.5-5 3.9 0 2.2 1.7 3.1 4.6 3.7 2.6.5 3.4.9 3.4 1.9 0 1-1 1.7-2.6 1.7-1.8 0-3.1-.8-4-2l-2.4 1.9c1.3 1.9 3.5 2.8 6.2 2.8 3.3 0 5.4-1.6 5.4-4.2 0-2.4-1.7-3.3-4.8-3.9-2.4-.5-3.2-.8-3.2-1.7 0-.9.9-1.5 2.3-1.5 1.5 0 2.7.6 3.5 1.7l2.5-2z"
          fill="#05080f"
        />
      </svg>
    </span>
  );
}

// Product lockup (default): [S] SDIP AI  +  subordinate ATLAS engine label.
// variant="corporate": [S] SDIP TECHNOLOGIES (parent lineage).
// compact: mark + "SDIP AI" only.
export function BrandLogo({
  to = "/",
  compact = false,
  variant = "product",
}: {
  to?: string;
  compact?: boolean;
  variant?: "product" | "corporate";
}) {
  return (
    <Link to={to} className="flex items-center gap-2.5" data-testid="brand-logo">
      <SdipMark size={compact ? 26 : 30} />
      {variant === "corporate" ? (
        <span className="font-display text-[15px] font-extrabold tracking-tight text-foreground">
          SDIP <span className="font-bold text-muted-foreground">TECHNOLOGIES</span>
        </span>
      ) : compact ? (
        <span className="font-display text-[15px] font-extrabold tracking-tight text-foreground">
          SDIP <span className="text-atlas-400">AI</span>
        </span>
      ) : (
        <span className="leading-tight">
          <span className="block font-display text-[15px] font-extrabold tracking-tight text-foreground">
            SDIP <span className="text-atlas-400">AI</span>
          </span>
          <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">ATLAS</span>
        </span>
      )}
    </Link>
  );
}
